import React from 'react';
import { cn } from '@/lib/utils';

export type PlatformCheckoutShellAsideVariant = 'checkout' | 'onboarding';

export interface PlatformCheckoutShellProps {
  /** Summary / product panel (left on desktop, top on mobile) */
  left: React.ReactNode;
  /** Primary interactive content (right on desktop, below on mobile) */
  children: React.ReactNode;
  /** Extra classes on the outer wrapper */
  className?: string;
  /**
   * `checkout` — narrow summary column (e.g. plan selection). `onboarding` — summary rail aligned
   * toward the main column (register / academy setup).
   */
  asideVariant?: PlatformCheckoutShellAsideVariant;
}

/**
 * Full-viewport two-column layout modeled on Stripe Checkout: summary left (tinted),
 * form/content right (white), single vertical divider, no outer card chrome.
 */
export function PlatformCheckoutShell({
  left,
  children,
  className,
  asideVariant = 'checkout',
}: PlatformCheckoutShellProps) {
  const isOnboarding = asideVariant === 'onboarding';

  return (
    <div
      className={cn(
        /* max-h + overflow-hidden: avoid body + inner <main> both scrolling (double scrollbar) */
        'flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-background',
        className
      )}
      data-testid="checkout-container"
    >
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Below lg: one scroll surface so branding + form read as a single column */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:contents">
          <aside
            className={cn(
              'flex w-full shrink-0 flex-col overflow-visible px-4 pt-6 pb-5 sm:px-6 sm:pt-8 sm:pb-6 lg:min-h-0 lg:overflow-y-auto lg:px-8 lg:py-10',
              /* Tinted summary rail (same surface as desktop) + soft edge toward main */
              'border-0 bg-muted/40 dark:bg-muted/25',
              'shadow-[inset_0_-10px_22px_-12px_rgba(15,23,42,0.05)] dark:shadow-[inset_0_-10px_22px_-12px_rgba(0,0,0,0.22)]',
              'lg:border-r lg:border-border',
              'lg:shadow-[inset_-14px_0_30px_-12px_rgba(15,23,42,0.07)] lg:dark:shadow-[inset_-16px_0_36px_-12px_rgba(0,0,0,0.32)]',
              'lg:max-w-none lg:w-[38%]'
            )}
          >
            <div
              className={cn(
                'flex w-full',
                isOnboarding ? 'justify-center lg:justify-end' : 'justify-center'
              )}
            >
              <div className="mr-0 min-w-0 w-full max-w-sm lg:mr-10 lg:w-auto lg:max-w-[20rem] xl:max-w-[26rem]">
                {left}
              </div>
            </div>
          </aside>
          <main
            className={cn(
              'min-h-0 flex-1 bg-background px-4 pt-5 pb-10 sm:px-6 sm:pt-6 sm:pb-12 lg:min-h-0 lg:overflow-y-auto lg:px-14 lg:py-12',
              isOnboarding && 'lg:flex lg:flex-col lg:items-center'
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
